import axios from 'axios';
import { message } from 'antd';
import {
  enqueueOfflineMutation,
  peekOfflineQueue,
  replaceOfflineQueue,
  recordFailedOfflineMutation,
  currentUserId,
} from './offlineQueue';
import { clearCachedApiData } from '../utils/clearCachedApiData';
import { isPublicAppPath } from '../utils/publicAppPaths';

// Port 8000 is used by another local project (khet/backend). Maveshi API runs on 8010.
//const API_BASE_URL = 'http://127.0.0.1:8000/api';
//export const BACKEND_URL = 'http://127.0.0.1:8000';

const API_BASE_URL = 'https://api.maveshiwala.com/api';
export const BACKEND_URL = 'https://api.maveshiwala.com';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

let isFlushingOfflineQueue = false;

export async function flushOfflineQueue() {
  if (isFlushingOfflineQueue) return;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return;

  // Only send the logged-in user's own changes; other users' changes wait for them.
  // (Items saved before owners were recorded have no userId and are sent as before.)
  const userId = currentUserId();
  if (!userId) return;
  const isMine = (item) => item.userId == null || item.userId === userId;
  const mine = peekOfflineQueue().filter(isMine);
  if (mine.length === 0) return;

  isFlushingOfflineQueue = true;
  let synced = 0;
  let rejected = 0;
  try {
    for (const next of mine) {
      if (typeof navigator !== 'undefined' && navigator.onLine === false) break;

      try {
        // Bypass offline adapter/queueing to avoid loops
        await api.request({
          url: next.url,
          method: next.method,
          data: next.data,
          params: next.params,
          headers: next.headers,
          __skipOfflineQueue: true,
        });
        synced += 1;
      } catch (err) {
        const status = err.response?.status;
        // Network/server problem: stop and retry later. Rejected by the server: set aside, carry on.
        if (!status || status >= 500 || status === 401) break;
        recordFailedOfflineMutation(next, status, err.response?.data?.message);
        rejected += 1;
      }
      replaceOfflineQueue(peekOfflineQueue().filter((item) => item.id !== next.id));
    }

    if (rejected > 0) {
      message.warning(`${rejected} offline change(s) could not be saved and were skipped. Please re-enter them.`);
    }
    if (synced > 0 && synced + rejected === mine.length) {
      message.success('Back online. Offline changes synced.');
    } else if (synced > 0) {
      message.info('Back online. Some offline changes synced.');
    }
  } finally {
    isFlushingOfflineQueue = false;
  }
}

// Add token to requests if available
api.interceptors.request.use(
  (config) => {
    // Offline mutation queue: accept writes while offline and sync later.
    const method = (config.method || 'get').toLowerCase();
    const isMutation = ['post', 'put', 'patch', 'delete'].includes(method);
    const isOffline = typeof navigator !== 'undefined' && navigator.onLine === false;
    const skipOfflineQueue = config.__skipOfflineQueue === true;

    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Don't queue auth endpoints
    const urlStr = `${config.baseURL || ''}${config.url || ''}`;
    const isAuthRequest =
      urlStr.includes('/login') ||
      urlStr.includes('/register') ||
      urlStr.includes('/forgot-password') ||
      urlStr.includes('/reset-password');

    if (isMutation && isOffline && !skipOfflineQueue && !isAuthRequest) {
      // Store a minimal request to replay later.
      const count = enqueueOfflineMutation({
        url: config.url,
        method,
        data: config.data,
        params: config.params,
        headers: {
          // include auth header if present
          Authorization: config.headers?.Authorization,
          'Content-Type': config.headers?.['Content-Type'] || 'application/json',
          Accept: config.headers?.Accept || 'application/json',
        },
      });

      message.warning(`You're offline. Saved locally and will sync when online. (${count} pending)`);

      // Resolve immediately so UI doesn't show "failed to save"
      config.adapter = async () => {
        return {
          data: { queued: true, offline: true },
          status: 202,
          statusText: 'Accepted',
          headers: {},
          config,
          request: {},
        };
      };
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle token expiration and upgrade redirects
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Only clear auth if it's not a login/register request
      const isAuthRequest = error.config?.url?.includes('/login') ||
        error.config?.url?.includes('/register');

      if (!isAuthRequest) {
        // A worker who signed in with a PIN goes back to their farm's worker sign-in, not the email login
        let workerLink = null;
        try {
          const storedUser = JSON.parse(localStorage.getItem('user') || 'null');
          workerLink = storedUser?.worker_mode ? localStorage.getItem('worker_link') : null;
        } catch {
          workerLink = null;
        }
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        clearCachedApiData();
        // Public pages (e.g. QR field report) must stay usable without login.
        if (!isPublicAppPath(window.location.pathname)) {
          if (window.location.pathname !== '/login' && window.location.pathname !== '/register') {
            window.location.href = workerLink ? `/w/${workerLink}` : '/login';
          }
        }
      }
    }

    // Handle account locked or upgrade required errors
    if (error.response?.status === 403 && (error.response?.data?.account_locked || error.response?.data?.upgrade_required)) {
      // Account is locked or upgrade required - redirect to upgrade page
      const upgradeUrl = error.response.data.upgrade_url || '/upgrade';
      const currentPath = window.location.pathname;

      // Check if this is an allowed endpoint that should work even when locked
      const allowedEndpoints = ['/packages/active', '/upgrade-requests', '/subscriptions/current'];
      const isAllowedEndpoint = allowedEndpoints.some(endpoint => error.config?.url?.includes(endpoint));

      // Only dispatch event if not already on upgrade page and not an allowed endpoint
      // Also check if path starts with upgrade to handle query params
      if (!currentPath.startsWith('/upgrade') && !isAllowedEndpoint) {
        // Dispatch custom event for Layout component to handle navigation
        // Layout component will use React Router's navigate() to avoid page refresh
        window.dispatchEvent(new CustomEvent('account-locked', {
          detail: { upgradeUrl }
        }));
      }
      // If already on upgrade page or allowed endpoint, don't do anything - just let the error be handled by the component
    }

    // Handle upgrade required errors - let components handle navigation
    // Components will use React Router's navigate() to avoid full page refresh
    // The error will be passed to the component's catch block

    return Promise.reject(error);
  }
);

export default api;

